import { Component, computed, inject } from '@angular/core';
import {
  TaskViewGroupChange,
  TaskViewSettingChange,
  TaskViewSettingSection,
  TaskViewSettingsComponent
} from '@app/components/task/task-view-settings/task-view-settings.component';
import { TaskFiltersService } from '../task-filters.service';

const MOBILE_TASK_VIEW_SETTINGS_SECTIONS: readonly TaskViewSettingSection[] = [
  {
    kind: 'choice',
    key: 'avatar',
    labelKey: 'task-list-view-settings.appearance',
    toggleable: true,
    options: [
      { key: 'none', labelKey: 'task-list-view-settings.hidden' },
      { key: 'task', labelKey: 'project-tasks.list.column-headers.avatar' },
      { key: 'participants', labelKey: 'project-tasks.list.column-headers.participants' }
    ]
  },
  {
    kind: 'choice',
    key: 'date',
    labelKey: 'task-filters.sections.dates',
    toggleable: true,
    options: [
      { key: 'none', labelKey: 'task-list-view-settings.hidden' },
      { key: 'start', labelKey: 'project-tasks.list.column-headers.start-date' },
      { key: 'end', labelKey: 'project-tasks.list.column-headers.end-date' }
    ]
  },
  {
    kind: 'toggles',
    key: 'task-data',
    labelKey: 'task-filters.sections.taskData',
    toggleable: true,
    options: [
      { key: 'status', labelKey: 'project-tasks.list.column-headers.status' },
      { key: 'priority', labelKey: 'project-tasks.list.column-headers.priority' }
    ]
  }
];

/**
 * PROPOZYCJA — DO WERYFIKACJI: one presentation menu for mobile cards and compact.
 * It shares the route-scoped state with their task rows; table columns keep their own manager.
 * Task details use a separate adapter and do not read these signals.
 * @summary Configures visible task-row elements without changing task filters or fetched data.
 * @selector proffeo-task-mobile-view-settings
 */
@Component({
  selector: 'proffeo-task-mobile-view-settings',
  templateUrl: './task-mobile-view-settings.component.html',
  host: { class: 'inline-flex shrink-0' },
  imports: [TaskViewSettingsComponent]
})
export class TaskMobileViewSettingsComponent {
  private readonly filters = inject(TaskFiltersService);

  protected readonly sections = MOBILE_TASK_VIEW_SETTINGS_SECTIONS;
  protected readonly values = computed(() => ({
    avatar: this.filters.mobileAvatarMode(),
    date: this.filters.mobileDateMode(),
    status: this.filters.mobileStatusVisible(),
    priority: this.filters.mobilePriorityVisible()
  }));
  protected readonly groupEnabled = computed(() => ({
    avatar: this.filters.mobileAvatarMode() !== 'none',
    date: this.filters.mobileDateMode() !== 'none',
    'task-data': this.filters.mobileStatusVisible() || this.filters.mobilePriorityVisible()
  }));

  protected updateSetting(change: TaskViewSettingChange): void {
    switch (change.key) {
      case 'avatar':
        if (change.value === 'none' || change.value === 'task' || change.value === 'participants') {
          if (change.value !== 'none') {
            this.filters.mobileLastAvatarMode.set(change.value);
          }
          this.filters.mobileAvatarMode.set(change.value);
        }
        break;
      case 'date':
        if (change.value === 'none' || change.value === 'start' || change.value === 'end') {
          if (change.value !== 'none') {
            this.filters.mobileLastDateMode.set(change.value);
          }
          this.filters.mobileDateMode.set(change.value);
        }
        break;
      case 'status':
        if (typeof change.value === 'boolean') {
          if (change.value || this.filters.mobilePriorityVisible()) {
            this.filters.mobileLastTaskData.set({
              status: change.value,
              priority: this.filters.mobilePriorityVisible()
            });
          }
          this.filters.mobileStatusVisible.set(change.value);
        }
        break;
      case 'priority':
        if (typeof change.value === 'boolean') {
          if (change.value || this.filters.mobileStatusVisible()) {
            this.filters.mobileLastTaskData.set({
              status: this.filters.mobileStatusVisible(),
              priority: change.value
            });
          }
          this.filters.mobilePriorityVisible.set(change.value);
        }
        break;
    }
  }

  protected toggleGroup(change: TaskViewGroupChange): void {
    switch (change.key) {
      case 'avatar': {
        const current = this.filters.mobileAvatarMode();
        if (!change.enabled && current !== 'none') {
          this.filters.mobileLastAvatarMode.set(current);
        }
        this.filters.mobileAvatarMode.set(change.enabled ? this.filters.mobileLastAvatarMode() : 'none');
        break;
      }
      case 'date': {
        const current = this.filters.mobileDateMode();
        if (!change.enabled && current !== 'none') {
          this.filters.mobileLastDateMode.set(current);
        }
        this.filters.mobileDateMode.set(change.enabled ? this.filters.mobileLastDateMode() : 'none');
        break;
      }
      case 'task-data': {
        const status = this.filters.mobileStatusVisible();
        const priority = this.filters.mobilePriorityVisible();
        if (!change.enabled && (status || priority)) {
          this.filters.mobileLastTaskData.set({ status, priority });
        }
        const restored = this.filters.mobileLastTaskData();
        this.filters.mobileStatusVisible.set(change.enabled ? restored.status : false);
        this.filters.mobilePriorityVisible.set(change.enabled ? restored.priority : false);
        break;
      }
    }
  }
}
